import { Form, useFormApi } from "informedv4";
import * as React from "react";
import { Link } from "react-router-dom";
import { User, Site } from "webportalclient";
import { useCurrentStudyConfigurationContext } from "../Core/CurrentStudyConfigurationContext";
import { useSecurityContext } from "../Core/SecurityContext";
import { ErrorBoundary } from "../errors/ErrorBoundary";
import { MultiSelect } from "../forms/informedv4/MultiSelectV4";
import { Required } from "../forms/validation/Required";
import { PermissionEnum } from "../misc/api/ApiPermissionEnum";
import { useWebPortalApiClient } from "../misc/api/WebPortalTokenClient";
import Alert, { AlertCategoryEnum } from "../misc/components/Alert";
import AsyncActionButton from "../misc/components/AsyncActionButton";
import { Authorization } from "../misc/components/Authorization";

export interface AssociateUsersWithSitesFormState {
    sites: Site[];
    users: User[];
}

export const AssociateUsersWithSitesForm = () => {
    const { study } = useCurrentStudyConfigurationContext();
    const { currentUserPermissions } = useSecurityContext();
    const client = useWebPortalApiClient();
    const [siteOptions, setSiteOptions] = React.useState([]);
    const [userOptions, setUserOptions] = React.useState([]);
    const [submitting, setSubmitting] = React.useState<boolean>(false);
    const [errorAlert, setErrorAlert] = React.useState<string>(null);
    const [successAlert, setSuccessAlert] = React.useState<boolean>(false);
    const formApi = useFormApi();

    React.useEffect(() => {
        const getOptions = async () => {
            const sitesResult = await client.client.getSites("id,number", `studyId eq ${study.id}`);
            setSiteOptions(sitesResult.value);

            const usersResult = await client.client.getUsers("id,email");
            setUserOptions(usersResult.value);
        }

        getOptions();
    }, []);

    const onAssociateClick = async () => {
        setSubmitting(true);
        setErrorAlert(null);
        setSuccessAlert(false);
        formApi.validate();
        const formState = formApi.getFormState();

        if (formState.invalid) {
            setSubmitting(false);
            return setErrorAlert("Errors exist on the page");
        }

        try {
            for (const user of formState.values.users as any) {
                const userWithExistingSites = await client.client.getUser(user.id, "siteIds");

                const patchResult = await client.client.patchUser({
                    siteIds: [...userWithExistingSites.siteIds, ...(formState.values.sites as any).map(s => s.id)],
                }, user.id);
            }
        } catch (ex) {
            setErrorAlert(ex.message);
            setSubmitting(false);
            return;
        }

        setSubmitting(false);
        setSuccessAlert(true);
    }

    return (
        <Authorization permissionId={PermissionEnum.AssociateUsersWithSites}
            studyId={study.id}
            currentPermissions={currentUserPermissions}
        >
            <ErrorBoundary>
                <Alert show={successAlert} category={AlertCategoryEnum.Success}>
                    Saved successfully. Return to{" "}
                    <Link to={`/users`}>Users list</Link>?
                </Alert>
                <Alert show={errorAlert !== null} category={AlertCategoryEnum.Danger}>
                    {errorAlert}
                </Alert>
                <div className="row">
                    <div className="col-md-6">
                        <h2 className="text-primary">Associate Users with Sites</h2>
                    </div>
                </div>
                <div className="row">
                    <div className="col-md-12">
                        <div className="panel panel-default">
                            <div className="panel-body">
                                <div className="row">
                                    <div className="col-sm-6 col-xs-12 form-group">
                                        <label>Sites to associate</label>
                                        <MultiSelect
                                            name="sites"
                                            id="sitesSelect"
                                            field="sites"
                                            labelKey="number"
                                            valueKey="id"
                                            multi
                                            options={siteOptions}
                                            validate={Required}
                                        />
                                    </div>
                                    <div className="col-sm-6 col-xs-12 form-group">
                                        <label>Users to associate</label>
                                        <MultiSelect
                                            name="users"
                                            id="usersSelect"
                                            field="users"
                                            labelKey="email"
                                            valueKey="id"
                                            multi
                                            options={userOptions}
                                            validate={Required}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="row">
                    <div className="col-md-12">
                        <div className="pull-right">
                            <AsyncActionButton className="btn btn-primary" type="submit" isSubmitting={submitting} disabled={submitting} onClick={onAssociateClick}>
                                Associate
                            </AsyncActionButton>&nbsp;
                            <Link className="btn btn-primary btn-outline" to="/users" data-automationid={`ReturnToList`}>
                                Return to List
                            </Link>
                        </div>
                    </div>
                </div>
            </ErrorBoundary>
        </Authorization>
    )
}

export const AssociateUsersWithSites = () => {
    return (
        <Form validateOn="submit">
            <AssociateUsersWithSitesForm />
        </Form>
    );
}
